/* eslint-disable jsx-a11y/alt-text */
import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  List,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@mui/material";
import { ThemeProvider } from "styled-components";

import DehazeIcon from "@mui/icons-material/Dehaze";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LogoutIcon from "@mui/icons-material/Logout";
import DownloadIcon from "@mui/icons-material/Download";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import styled from "styled-components";

import theme from "../theme/Theme";
import befly from "../../assets/images/befly.png";
import manual from "../../assets/files/manual.pdf";

import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";

import { useAuthorization } from "../hooks/useAuthorization";
import {
  removeUserAuthenticate,
  isUserAuthenticate,
  setSessionExpired,
} from "../api/services/AuthService";

import MenuIcon from "@mui/icons-material/Menu";

import { LinksMenu } from "./Util.jsx";

/* =======================
   STYLES
======================= */

const Draw = styled.div`
  background-color: #041e42;
  height: 80px;
  width: 100%;
  position: fixed;
  top: 0;
  z-index: 10;

  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;

  box-shadow: 0 -9px 20px 9px #000;
`;

const MenuTitle = styled.div`
  background-color: transparent !important;
  color: #041e42;
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 1px;
  text-transform: uppercase;

  display: flex;
  align-items: center;
  gap: 8px;

  line-height: 48px;
  padding-left: 16px;
`;

const Airlines = styled(FlightTakeoffIcon)`
  font-size: 18px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 2;
`;

const HeaderCenter = styled.div`
  flex: 1;
  display: flex;
  flex: 3;
  justify-content: center;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 2;
  position: relative;
`;

const BtnMenu = styled.button`
  background-color: #041e42;
  color: #fff;
  border: 1px solid #fff;

  margin-left: 10px;
  padding: 4px;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;

  position: relative;
  z-index: 1;

  border-radius: 8px;

  box-shadow:
    3px 3px 6px rgba(0, 0, 0, 0.3),
    -3px -3px 6px rgba(255, 255, 255, 0.15);

  transition:
    transform 0.15s ease,
    box-shadow 0.2s ease;

  &:active {
    transform: scale(0.94);
    box-shadow:
      inset 3px 3px 6px rgba(0, 0, 0, 0.4),
      inset -3px -3px 6px rgba(255, 255, 255, 0.1);
  }
`;
const ImagemLogo = styled.img`
  width: auto;
  height: 70px;
`;

const Logo = styled.h2`
  color: white;
  font-size: 40px;
  font-weight: bold;
  margin: 0;
`;

const Saudacao = styled.div`
  position: absolute;
  bottom: -25px;
  left: 45%;
  color: white;
  font-size: 12px;
  font-style: italic;
  font-weight: 700;
  white-space: nowrap;
`;

const DownloadManualLink = styled.a`
  color: white;
  text-decoration: none;
  font-size: 14px;

  margin-right: 20px;
  display: flex;
  align-items: center;
  gap: 5px;
  position: relative;

  transition: color 0.3s ease-in-out;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 0%;
    height: 2px;
    background-color: #ff9800;
    transition: width 0.25s ease;
  }

  &:hover {
    color: #ff9800;
  }

  &:hover::after {
    width: 100%;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const LinkMenu = styled(Link)`
  margin: 0;
  font-weight: 400;
  font-size: 1rem;
  display: block;
  color: #041e42 !important;
  text-decoration: none;
`;

const LogoWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

const DrawerItem = styled(ListItem)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  border-radius: 6px;
  transition: all 0.25s ease;

  &:hover {
    background-color: rgba(4, 30, 66, 0.08);
    padding-left: 10px;
    transform: translateX(2px);
    font-size: 1.05rem;
    cursor: pointer;
  }
`;

const LabelsMenu = styled(ListItemText)`
  color: #041e42;
`;

const IconsMenu = styled(ListItemIcon)`
  min-width: 36px;
  color: #041e42;
  justify-content: flex-start;
`;

const activeItemStyle = {
  backgroundColor: "#FFDEAD",
  borderLeft: "4px solid #041E42",
};

/* =======================
   COMPONENT
======================= */

export default function TemporaryDrawer() {
  const anchor = "left";
  const navigate = useNavigate();
  const location = useLocation();

  const [salutation, setSalutation] = useState("");
  const [openFaturas, setOpenFaturas] = useState(false);
  const [state, setState] = useState({ left: false });

  useEffect(() => {
    const jsonToken = isUserAuthenticate();

    if (!jsonToken) {
      navigate("/login");
      return;
    }

    const objToken = jwtDecode(jsonToken.token);
    setSalutation(objToken.name);

    if (location.pathname.startsWith("/fatura")) {
      setOpenFaturas(true);
    }
  }, [navigate, location.pathname]);

  const logout = () => {
    setSessionExpired();
    removeUserAuthenticate();
    navigate("/login");
  };

  const toggleDrawer = (open) => () => {
    setState({ left: open });
  };

  const isActive = (path) => path && location.pathname.startsWith(path);

  const { canAccess } = useAuthorization();
  const menuPermitido = LinksMenu.map((item) => {
    if (item.children) {
      const childrenPermitidos = item.children.filter((child) =>
        canAccess({
          view: child.view,
          roles: child.roles,
        }),
      );

      if (childrenPermitidos.length === 0) return null;

      return { ...item, children: childrenPermitidos };
    }

    return canAccess({
      view: item.view,
      roles: item.roles,
    })
      ? item
      : null;
  }).filter(Boolean);

  /* =======================
     DRAWER CONTENT
  ======================= */

  const drawerContent = (
    <Box
      sx={{
        width: 250,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flex: 1 }}>
        <List>
          <MenuTitle component="div">
            <MenuIcon sx={{ fontSize: 18 }} />
            Menu
          </MenuTitle>
          <Divider />
          {menuPermitido.map((menu, index) => {
            // MENU COM FILHOS (COLLAPSE)
            if (menu.children) {
              return (
                <React.Fragment key={index}>
                  <ListItem
                    button
                    onClick={() => setOpenFaturas(!openFaturas)}
                    sx={openFaturas ? activeItemStyle : {}}
                  >
                    <IconsMenu>{menu.icon}</IconsMenu>
                    <LabelsMenu primary={menu.label} />
                    {openFaturas ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>

                  <Collapse in={openFaturas} unmountOnExit>
                    <List component="div" disablePadding>
                      {menu.children.map((child, idx) => (
                        <LinkMenu to={child.url} key={idx}>
                          <DrawerItem
                            button
                            sx={{
                              pl: 4,
                              ...(isActive(child.url) ? activeItemStyle : {}),
                            }}
                            onClick={toggleDrawer(false)}
                          >
                            <IconsMenu sx={{ minWidth: 36 }}>
                              {child.icon}
                            </IconsMenu>
                            <LabelsMenu primary={child.label} />
                          </DrawerItem>
                        </LinkMenu>
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            }

            // MENU SIMPLES
            return (
              <LinkMenu to={menu.url} key={index}>
                <DrawerItem
                  button
                  onClick={toggleDrawer(false)}
                  sx={isActive(menu.url) ? activeItemStyle : {}}
                >
                  <IconsMenu>{menu.icon}</IconsMenu>
                  <LabelsMenu primary={menu.label} />
                </DrawerItem>
              </LinkMenu>
            );
          })}
        </List>

        <Divider />
      </Box>

      <Divider />

      <List>
        <ListItem button onClick={logout}>
          <ListItemIcon>
            <LogoutIcon sx={{ color: "#041E42" }} />
          </ListItemIcon>
          <LabelsMenu primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );

  /* =======================
     RENDER
  ======================= */

  return (
    <ThemeProvider theme={theme}>
      <Draw>
        <HeaderLeft>
          <BtnMenu onClick={toggleDrawer(true)}>
            <DehazeIcon sx={{ fontSize: 40 }} />
          </BtnMenu>
          <LogoWrapper>
            <ImagemLogo src={befly} />
          </LogoWrapper>
        </HeaderLeft>

        <HeaderCenter>
          <Logo>
            <Airlines /> PORTAL DE FATURAS
          </Logo>
        </HeaderCenter>

        <HeaderRight>
          <DownloadManualLink
            href={manual}
            target="_blank"
            download="Manual_Portal_do_agente.pdf"
          >
            <DownloadIcon sx={{ verticalAlign: "middle" }} />
            Manual Portal
          </DownloadManualLink>

          <Saudacao>Bem vindo(a) {salutation}</Saudacao>
        </HeaderRight>

        <Drawer anchor={anchor} open={state.left} onClose={toggleDrawer(false)}>
          {drawerContent}
        </Drawer>
      </Draw>
    </ThemeProvider>
  );
}
